/**
 * Swap execution utility — builds Universal Router calldata for V4 swaps.
 *
 * Implements the V4Planner → RoutePlanner → Universal Router pattern:
 * 1. Build pool key from token addresses
 * 2. V4Planner encodes SWAP_EXACT_IN_SINGLE + SETTLE_ALL + TAKE_ALL
 * 3. RoutePlanner wraps in V4_SWAP command
 * 4. Encode as Universal Router execute(commands, inputs, deadline)
 *
 * ARCH-24: Token amount handling (bigint on-chain)
 * Story 5.3 Task 1
 */

import { type Hex, concat, encodeAbiParameters, encodeFunctionData, toHex } from "viem";

import { universalRouterAbi } from "./abis/universal-router";
import { DYNAMIC_FEE_FLAG } from "./chain-addresses";

/** V4 Action constants (from @uniswap/v4-sdk Actions) */
const V4_SWAP_EXACT_IN_SINGLE = 0x06;
const V4_SETTLE = 0x0b;
const V4_SETTLE_ALL = 0x0c;
const V4_TAKE_ALL = 0x0f;

/** Universal Router command constants */
const COMMAND_WRAP_ETH = 0x0b;
const COMMAND_V4_SWAP = 0x10;

/** ADDRESS_THIS sentinel — the router resolves this to its own address. */
const ADDRESS_THIS = "0x0000000000000000000000000000000000000002" as const;

export type SwapParams = {
	tokenIn: `0x${string}`;
	tokenOut: `0x${string}`;
	amountIn: bigint;
	amountOutMinimum: bigint;
	/** The hooks contract address (poolAddress from token detail) */
	poolAddress: `0x${string}`;
	tickSpacing: number;
	zeroForOne: boolean;
	deadline: bigint;
	/** When true, msg.value = amountIn (buying tokens with native ETH) */
	isNativeEthInput?: boolean;
};

type SwapCalldata = {
	to: `0x${string}`;
	data: Hex;
	value: bigint;
};

/** Structured args for Universal Router execute — used by writeContract */
export type SwapArgs = {
	commands: Hex;
	inputs: readonly Hex[];
	deadline: bigint;
	value: bigint;
};

/**
 * Sort two addresses to determine token0 and token1.
 * token0 = min(a, b) by lowercase hex comparison.
 */
function sortTokens(
	a: `0x${string}`,
	b: `0x${string}`
): { token0: `0x${string}`; token1: `0x${string}` } {
	const aLower = a.toLowerCase();
	const bLower = b.toLowerCase();
	if (aLower < bLower) {
		return { token0: a, token1: b };
	}
	return { token0: b, token1: a };
}

/**
 * Encode SWAP_EXACT_IN_SINGLE params (ExactInputSingleParams struct).
 *
 * MUST encode as a tuple (not individual params). The CalldataDecoder's
 * decodeSwapExactInSingleParams does:
 *   swapParams := add(params.offset, calldataload(params.offset))
 *
 * This reads the first word as an OFFSET. Tuple encoding produces [0x20][fields...],
 * so the offset = 0x20, and swapParams points to the struct fields starting at
 * currency0. Individual-param encoding would put currency0 as the first word,
 * which gets misinterpreted as a huge offset → revert.
 */
function encodeSwapExactInSingle(
	token0: `0x${string}`,
	token1: `0x${string}`,
	fee: number,
	tickSpacing: number,
	hooks: `0x${string}`,
	zeroForOne: boolean,
	amountIn: bigint,
	amountOutMinimum: bigint
): Hex {
	return encodeAbiParameters(
		[
			{
				type: "tuple",
				components: [
					{ type: "address", name: "currency0" },
					{ type: "address", name: "currency1" },
					{ type: "uint24", name: "fee" },
					{ type: "int24", name: "tickSpacing" },
					{ type: "address", name: "hooks" },
					{ type: "bool", name: "zeroForOne" },
					{ type: "uint128", name: "amountIn" },
					{ type: "uint128", name: "amountOutMinimum" },
					{ type: "bytes", name: "hookData" },
				],
			},
		],
		[
			{
				currency0: token0,
				currency1: token1,
				fee,
				tickSpacing,
				hooks,
				zeroForOne,
				amountIn,
				amountOutMinimum,
				hookData: "0x",
			},
		]
	);
}

/**
 * Encode the V4Planner actions and params.
 *
 * Actions: SWAP_EXACT_IN_SINGLE, SETTLE or SETTLE_ALL, TAKE_ALL
 *
 * When isNativeEthInput=true, uses SETTLE with payerIsUser=false so the
 * router pays from its own WETH balance (deposited by the preceding WRAP_ETH
 * command). Otherwise uses SETTLE_ALL which pulls via Permit2 from msg.sender.
 *
 * Returns: abi.encode(bytes actions, bytes[] params)
 */
function encodeV4Plan(
	token0: `0x${string}`,
	token1: `0x${string}`,
	fee: number,
	tickSpacing: number,
	hooks: `0x${string}`,
	zeroForOne: boolean,
	amountIn: bigint,
	amountOutMinimum: bigint,
	tokenIn: `0x${string}`,
	tokenOut: `0x${string}`,
	isNativeEthInput: boolean
): Hex {
	const settleAction = isNativeEthInput ? V4_SETTLE : V4_SETTLE_ALL;

	const actions = concat([
		toHex(V4_SWAP_EXACT_IN_SINGLE, { size: 1 }),
		toHex(settleAction, { size: 1 }),
		toHex(V4_TAKE_ALL, { size: 1 }),
	]);

	const swapParam = encodeSwapExactInSingle(
		token0,
		token1,
		fee,
		tickSpacing,
		hooks,
		zeroForOne,
		amountIn,
		amountOutMinimum
	);

	// SETTLE (payerIsUser=false): router pays from its own WETH (wrapped via WRAP_ETH)
	// SETTLE_ALL: pulls from msg.sender via Permit2
	const settleParam = isNativeEthInput
		? encodeAbiParameters(
				[{ type: "address" }, { type: "uint256" }, { type: "bool" }],
				[tokenIn, amountIn, false]
			)
		: encodeAbiParameters([{ type: "address" }, { type: "uint256" }], [tokenIn, amountIn]);

	// TAKE_ALL: (address currency, uint256 minAmount)
	// Pass amountOutMinimum as the floor to enforce slippage protection at settlement
	const takeParam = encodeAbiParameters(
		[{ type: "address" }, { type: "uint256" }],
		[tokenOut, amountOutMinimum]
	);

	return encodeAbiParameters(
		[{ type: "bytes" }, { type: "bytes[]" }],
		[actions, [swapParam, settleParam, takeParam]]
	);
}

/**
 * Build structured swap args for Universal Router execute.
 * Returns { commands, inputs, deadline, value } for use with writeContract.
 *
 * For native ETH buy: prepends WRAP_ETH command to convert msg.value → WETH
 * in the router, then V4_SWAP uses SETTLE(payerIsUser=false) so the router
 * pays from its own balance.
 */
export function buildSwapArgs(params: SwapParams): SwapArgs {
	const {
		tokenIn,
		tokenOut,
		amountIn,
		amountOutMinimum,
		poolAddress,
		tickSpacing,
		zeroForOne,
		deadline,
		isNativeEthInput = false,
	} = params;

	const { token0, token1 } = sortTokens(tokenIn, tokenOut);

	const v4Plan = encodeV4Plan(
		token0,
		token1,
		DYNAMIC_FEE_FLAG,
		tickSpacing,
		poolAddress,
		zeroForOne,
		amountIn,
		amountOutMinimum,
		tokenIn,
		tokenOut,
		isNativeEthInput
	);

	// V4_SWAP input is the v4Plan directly — NOT wrapped in abi.encode(bytes).
	// The SDK's RoutePlanner passes v4Planner.finalize() raw (Parser.V4Actions).
	// The Universal Router's Dispatcher calls _executeActions(input) which passes
	// it to poolManager.unlock(). The _unlockCallback then calls
	// decodeActionsRouterParams() which expects abi.encode(bytes, bytes[]) layout
	// (first word = 0x40). Wrapping in abi.encode(bytes) would prepend 0x20, breaking it.

	if (isNativeEthInput) {
		// WRAP_ETH: wrap msg.value → WETH in the router's balance
		// Input: (address recipient, uint256 amountMin)
		const wrapInput = encodeAbiParameters(
			[{ type: "address" }, { type: "uint256" }],
			[ADDRESS_THIS, amountIn]
		);

		const commands: Hex = concat([
			toHex(COMMAND_WRAP_ETH, { size: 1 }),
			toHex(COMMAND_V4_SWAP, { size: 1 }),
		]);

		return {
			commands,
			inputs: [wrapInput, v4Plan],
			deadline,
			value: amountIn,
		};
	}

	const commands: Hex = toHex(COMMAND_V4_SWAP, { size: 1 });

	return {
		commands,
		inputs: [v4Plan],
		deadline,
		value: BigInt(0),
	};
}

/**
 * Build Universal Router calldata for a V4 swap.
 *
 * @param params - Swap parameters including tokens, amounts, pool info
 * @returns Transaction data: { to, data, value }
 */
export function buildSwapCalldata(params: SwapParams, routerAddress: `0x${string}`): SwapCalldata {
	const { commands, inputs, deadline, value } = buildSwapArgs(params);

	const data = encodeFunctionData({
		abi: universalRouterAbi,
		functionName: "execute",
		args: [commands, inputs, deadline],
	});

	return {
		to: routerAddress,
		data,
		value,
	};
}
